import { useCallback, useEffect, useRef } from 'react';

const MIN_FONT = 6;
const MAX_FONT = 48;
const PADDING_PX = 4;
const SAFETY_MARGIN_PX = 14;

/**
 * Finds the largest font size (between min and max) such that the content
 * fits inside the container without vertical overflow. Updates content.style.fontSize.
 * This hook is explicitly designed for non-scrollable boxes (like presenter notes).
 */
function fitFontSize(container, content, minFont = MIN_FONT, maxFont = MAX_FONT) {
  if (!container || !content) return;
  const maxHeight = container.clientHeight - PADDING_PX - SAFETY_MARGIN_PX;
  if (maxHeight <= 0) return;

  let low = minFont;
  let high = maxFont;
  let best = minFont;

  // Binary search: largest font size where content fits
  while (high - low > 1) {
    const mid = Math.round((low + high) / 2);
    content.style.fontSize = `${mid}px`;
    if (content.scrollHeight <= maxHeight) {
      best = mid;
      low = mid;
    } else {
      high = mid;
    }
  }
  // Final check at high
  content.style.fontSize = `${high}px`;
  if (content.scrollHeight <= maxHeight) best = high;
  else {
    content.style.fontSize = `${low}px`;
    if (content.scrollHeight <= maxHeight) best = low;
  }
  content.style.fontSize = `${best}px`;

  // Safety pass: if we are still a bit too tall (last line clipping),
  // step down 1px at a time until everything fits or we hit minFont.
  while (best > minFont && content.scrollHeight > maxHeight) {
    best -= 1;
    content.style.fontSize = `${best}px`;
  }
}

/**
 * Hook: measure container and content, set content font-size so text fits
 * the box (no scroll). Re-runs when container is resized or contentKey changes.
 * @param {React.RefObject} containerRef - ref to the scrollable container (notes section)
 * @param {React.RefObject} contentRef - ref to the content div (notes text)
 * @param {string|number} contentKey - change when notes content changes (e.g. slide id + index)
 * @param {{ minFontSize?: number, maxFontSize?: number }} options
 */
export function useFitTextToBox(containerRef, contentRef, contentKey, options = {}) {
  const minFont = options.minFontSize ?? MIN_FONT;
  const maxFont = options.maxFontSize ?? MAX_FONT;
  const rafRef = useRef(null);

  const runFit = useCallback(() => {
    if (rafRef.current != null) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    // Double rAF so we measure after layout (important when content just changed)
    const id1 = requestAnimationFrame(() => {
      const id2 = requestAnimationFrame(() => {
        rafRef.current = null;
        fitFontSize(containerRef.current, contentRef.current, minFont, maxFont);
      });
      rafRef.current = id2;
    });
    rafRef.current = id1;
  }, [containerRef, contentRef, minFont, maxFont]);

  useEffect(() => {
    const container = containerRef.current;
    const content = contentRef.current;
    if (!container || !content) return;

    runFit();

    const resizeObserver = new ResizeObserver(() => runFit());
    resizeObserver.observe(container);

    return () => {
      if (rafRef.current != null) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
      resizeObserver.disconnect();
    };
  }, [contentKey, runFit, containerRef, contentRef]);
}
